import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import "./product-details.css";
import ProductImageLightbox from "../components/ProductImageLightbox";
import PLACEHOLDER from "../assets/images/logo192.png";

const PRODUCT_API = process.env.REACT_APP_PRODUCT_SERVICE_URL;

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const getImageUrl = (storedName) =>
  storedName
    ? `https://afrogebeya.com/uploads/products/${String(storedName).replace(/\\/g, "/")}`
    : null;

const formatCurrency = (amount, currency = "ETB") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount || 0);

const Stars = ({ rating, size = 14 }) => {
  const full = Math.round(rating);
  const empty = 5 - full;
  return (
    <span style={{ fontSize: size, color: "#F59E0B", letterSpacing: 1 }}>
      {"★".repeat(full)}
      {"☆".repeat(empty)}
    </span>
  );
};

const SkeletonBlock = ({ h = 20, w = "100%", mb = 0 }) => (
  <span
    className="pd-skeleton pd-skeleton-block"
    style={{ height: h, width: w, marginBottom: mb, display: "block" }}
  />
);

/* ─────────────────────────────────────────
   Loading skeleton for the full page
───────────────────────────────────────── */
const PageSkeleton = () => (
  <div className="pd-container">
    <div className="pd-main">
      <div>
        <SkeletonBlock h={480} mb={12} />
        <div style={{ display: "flex", gap: 8 }}>
          {[...Array(4)].map((_, i) => (
            <SkeletonBlock key={i} h={72} w={72} />
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <SkeletonBlock h={32} w="80%" />
        <SkeletonBlock h={20} w="40%" />
        <SkeletonBlock h={64} />
        <SkeletonBlock h={120} />
        <SkeletonBlock h={160} />
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────
   Main component
───────────────────────────────────────── */
export default function ProductDetail() {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const { slug } = useParams();
  const productId = slug?.split("-").pop();
  const navigate = useNavigate();
  const { cart, addToCart, isInCart } = useCart();

  /* ── Data state ── */
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ── UI state ── */
  const [selectedAttrs, setSelectedAttrs] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [copied, setCopied] = useState(false);
  const [lensVisible, setLensVisible] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [zoomStyle, setZoomStyle] = useState({});
  const [zoomLevel, setZoomLevel] = useState(2.5);

  const imgRef = useRef(null);
  const zoomRef = useRef(null);

  /* ─────────────────────────────────────────
     Derived: variant selection
  ───────────────────────────────────────── */
  const variantAttrKeys = useMemo(() => {
    const keys = new Set();
    (product?.variants || []).forEach((v) =>
      Object.keys(v.attributes || {}).forEach((k) => keys.add(k)),
    );
    return [...keys];
  }, [product]);

  const attrOptionsMap = useMemo(() => {
    const map = {};
    variantAttrKeys.forEach((key) => {
      map[key] = [
        ...new Set(
          (product?.variants || [])
            .map((v) => v.attributes?.[key])
            .filter(Boolean),
        ),
      ];
    });
    return map;
  }, [product, variantAttrKeys]);

  /* Find variant matching all selected attrs */
  const selectedVariant = useMemo(() => {
    if (!product?.variants?.length) return null;

    const matched = product.variants.find((v) =>
      variantAttrKeys.every(
        (key) =>
          !selectedAttrs[key] || v.attributes?.[key] === selectedAttrs[key],
      ),
    );
    return (
      matched ||
      product.variants.find((v) => v.isDefault) ||
      product.variants[0]
    );
  }, [product, selectedAttrs, variantAttrKeys]);

  /* Is this attr value available given other current selections? */
  const isValueAvailable = useCallback(
    (key, value) =>
      (product?.variants || []).some((v) => {
        const othersMatch = Object.entries(selectedAttrs)
          .filter(([k]) => k !== key)
          .every(([k, val]) => v.attributes?.[k] === val);
        return othersMatch && v.attributes?.[key] === value;
      }),
    [product, selectedAttrs],
  );

  /* ─────────────────────────────────────────
     Derived: images for current selection
  ───────────────────────────────────────── */

  const currentImages = useMemo(() => {
    const variantImgs = (selectedVariant?.imageUrls || []).filter(Boolean);
    const productImgs = (product?.imageUrls || []).filter(Boolean);

    const main = selectedVariant?.mainImage || product?.mainImage;

    // Merge all images
    const merged = [...variantImgs, ...productImgs];

    // Remove duplicates
    const unique = [...new Set(merged)];

    // Ensure main image is first
    if (main) {
      return [main, ...unique.filter((img) => img !== main)];
    }

    return unique.length ? unique : [null];
  }, [selectedVariant, product]);

  /* ─────────────────────────────────────────
     Fetch product
  ───────────────────────────────────────── */
  useEffect(() => {
    if (!productId) return;
    let mounted = true;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const res = await axios.get(
          `${PRODUCT_API}/products/public/${productId}`,
        );
        if (!mounted) return;
        const prod = res.data;
        setProduct(prod);

        // Init variant selection from default variant
        const def =
          prod.variants?.find((v) => v.isDefault) || prod.variants?.[0];
        if (def) setSelectedAttrs(def.attributes || {});

        // Init main image
        const mainImg = def?.mainImage || prod.mainImage;
        setSelectedImage(mainImg ? getImageUrl(mainImg) : PLACEHOLDER);

        // Related products
        if (prod.categoryId) {
          const relRes = await axios.get(`${PRODUCT_API}/products/buyers`, {
            params: { categoryId: prod.categoryId, page: 0, size: 8 },
          });
          if (!mounted) return;
          setRelated(
            (relRes.data.content || []).filter(
              (p) => p.productId !== prod.productId,
            ),
          );
        }
      } catch {
        if (mounted) setError("Failed to load product details.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      mounted = false;
    };
  }, [productId]);

  /* Update selected image when variant changes */
  useEffect(() => {
    if (currentImages[0]) {
      setSelectedImage(getImageUrl(currentImages[0]) || PLACEHOLDER);
    }
  }, [selectedVariant]);

  /* ─────────────────────────────────────────
     Cart
  ───────────────────────────────────────── */
  const cartItem = cart.find((i) => i.variantId === selectedVariant?.id);
  const maxQty = selectedVariant?.availableQuantity ?? 0;
  const maxReached =
    cartItem && maxQty > 0 ? cartItem.quantity >= maxQty : false;

  const handleAddToCart = () => {
    if (!product || !selectedVariant) return;
    addToCart(product, quantity, selectedVariant);
    setQuantity(1);
  };

  /* FIX: original never called setQuantity */
  const decQty = () => setQuantity((q) => Math.max(1, q - 1));
  const incQty = () =>
    setQuantity((q) => {
      const remaining = maxQty - (cartItem?.quantity || 0);
      return Math.min(q + 1, remaining > 0 ? remaining : q + 1);
    });

  /* ─────────────────────────────────────────
     Image zoom & lens
  ───────────────────────────────────────── */
  const handleMouseMove = useCallback(
    (e) => {
      const rect = imgRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const xPct = (x / rect.width) * 100;
      const yPct = (y / rect.height) * 100;
      setLensPos({ x: x - 60, y: y - 60 });
      setZoomStyle({
        backgroundSize: `${zoomLevel * 100}%`,
        backgroundPosition: `${xPct}% ${yPct}%`,
      });
    },
    [zoomLevel],
  );

  /* FIX: single wheel handler, not duplicated */
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;
    const handler = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      setZoomLevel((prev) =>
        Math.min(Math.max(prev + (e.deltaY < 0 ? 0.2 : -0.2), 1.5), 4),
      );
    };
    img.addEventListener("wheel", handler, { passive: false });
    return () => img.removeEventListener("wheel", handler);
  }, []);

  /* ─────────────────────────────────────────
     Share
  ───────────────────────────────────────── */
  const shareUrl = `https://afrogebeya.com/api/v3/products/public/preview/${slug}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedName = encodeURIComponent(product?.productName || "");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* noop */
    }
  };

  /* ─────────────────────────────────────────
     Pricing
  ───────────────────────────────────────── */
  const currentPrice = selectedVariant?.price ?? product?.price ?? 0;

  const discountPrice = selectedVariant?.discountPrice;

  const showDiscount =
    discountPrice != null &&
    Number(discountPrice) > 0 &&
    Number(discountPrice) < Number(currentPrice);

  const displayPrice = showDiscount ? discountPrice : currentPrice;

  const currency = selectedVariant?.currency || product?.currency || "ETB";

  const discountPct = showDiscount
    ? Math.round((1 - Number(discountPrice) / Number(currentPrice)) * 100)
    : 0;

  /* ─────────────────────────────────────────
     Stock label
  ───────────────────────────────────────── */
  const stockQty = Number(selectedVariant?.availableQuantity ?? 0);
  const stockLabel =
    stockQty <= 0
      ? { cls: "pd-stock--out", text: "Out of Stock" }
      : stockQty <= 5
        ? { cls: "pd-stock--low", text: `Only ${stockQty} left` }
        : { cls: "pd-stock--in", text: `In Stock (${stockQty})` };

  /* ─────────────────────────────────────────
     Seller initial
  ───────────────────────────────────────── */
  const sellerInitial = (product?.storeName || "S")[0].toUpperCase();

  /* ─────────────────────────────────────────
     Tabs config
  ───────────────────────────────────────── */
  const TABS = [
    { id: "description", label: "Description" },
    { id: "specs", label: "Specifications" },
    { id: "reviews", label: "Reviews" },
  ];

  /* ─────────────────────────────────────────
     Render
  ───────────────────────────────────────── */
  if (loading)
    return (
      <div className="pd-page">
        <PageSkeleton />
      </div>
    );

  if (error || !product)
    return (
      <div
        className="pd-page pd-container"
        style={{ textAlign: "center", paddingTop: 80 }}
      >
        <p style={{ color: "#C0392B", marginBottom: 16 }}>
          {error || "Product not found."}
        </p>
        <button className="pd-submit-btn" onClick={() => navigate(-1)}>
          ← Go Back
        </button>
      </div>
    );

  return (
    <div className="pd-page">
      <div className="pd-container">
        {/* ── Breadcrumb ── */}
        <nav className="pd-breadcrumb" aria-label="breadcrumb">
          <Link to="/">Home</Link>
          <span className="pd-breadcrumb__sep">›</span>
          {product.categoryName && (
            <>
              <Link to={`/category/${product.categoryId}`}>
                {product.categoryName}
              </Link>
              <span className="pd-breadcrumb__sep">›</span>
            </>
          )}
          {product.subCategoryName && (
            <>
              <Link
                to={`/category/${product.categoryId}?sub=${product.subCategoryId}`}
              >
                {product.subCategoryName}
              </Link>
              <span className="pd-breadcrumb__sep">›</span>
            </>
          )}
          <span className="pd-breadcrumb__current">{product.productName}</span>
        </nav>

        {/* ══════════════════════════════════
            MAIN GRID
        ══════════════════════════════════ */}
        <div className="pd-main">
          {/* ── Left: Image gallery ── */}
          <div className="pd-gallery">
            {/* Thumbnails */}
            <div className="pd-gallery__thumbs">
              {currentImages.map((img, i) => {
                const url = img ? getImageUrl(img) : PLACEHOLDER;
                return (
                  <img
                    key={i}
                    src={url}
                    alt={`view-${i + 1}`}
                    className={`pd-gallery__thumb${
                      selectedImage === url ? " pd-gallery__thumb--active" : ""
                    }`}
                    onClick={() => setSelectedImage(url)}
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = PLACEHOLDER;
                    }}
                  />
                );
              })}
            </div>

            {/* Main image */}
            <div
              className="pd-gallery__main-wrap"
              onMouseEnter={() =>
                window.innerWidth > 1024 && setLensVisible(true)
              }
              onMouseLeave={() => setLensVisible(false)}
              onMouseMove={(e) =>
                window.innerWidth > 1024 && handleMouseMove(e)
              }
            >
              <img
                ref={imgRef}
                src={selectedImage || PLACEHOLDER}
                alt={product.productName}
                className="pd-gallery__img"
                onClick={() => {
                  const index = currentImages.findIndex(
                    (img) => getImageUrl(img) === selectedImage,
                  );

                  setLightboxIndex(index >= 0 ? index : 0);
                  setLightboxOpen(true);
                }}
                style={{ cursor: "zoom-in" }}
                onError={(e) => {
                  e.target.src = PLACEHOLDER;
                }}
                loading="lazy"
              />

              {/* Lens */}
              {lensVisible && (
                <div
                  className="pd-gallery__lens"
                  style={{ left: lensPos.x, top: lensPos.y }}
                />
              )}

              {/* Zoom box — rendered outside the clipped container via absolute + overflow visible on parent */}
              {lensVisible && (
                <div
                  ref={zoomRef}
                  className="pd-gallery__zoom"
                  style={{
                    backgroundImage: `url(${selectedImage})`,
                    ...zoomStyle,
                  }}
                />
              )}
            </div>
          </div>

          {/* ── Right: Product info ── */}
          <div className="pd-info">
            {/* Name + meta + share */}
            <div className="pd-info__header">
              <h1 className="pd-info__name">{product.productName}</h1>

              <div className="pd-info__meta">
                {(product.averageRating > 0 || product.totalRatings > 0) && (
                  <div className="pd-rating">
                    <Stars rating={product.averageRating ?? 0} size={15} />
                    <span className="pd-rating__count">
                      {(product.averageRating ?? 0).toFixed(1)} (
                      {product.totalRatings ?? 0})
                    </span>
                  </div>
                )}

                {product.totalSold > 0 && (
                  <>
                    <div className="pd-meta-sep" />
                    <span className="pd-sold">
                      <span>{product.totalSold}</span> sold
                    </span>
                  </>
                )}

                {/* Share buttons */}
                <div className="pd-share">
                  <a
                    href={`https://wa.me/?text=${encodedName}%20${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pd-share__btn"
                    title="WhatsApp"
                    style={{ color: "#25D366" }}
                  >
                    <i className="bi bi-whatsapp" />
                  </a>
                  <a
                    href={`https://t.me/share/url?url=${encodedUrl}&text=${encodedName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pd-share__btn"
                    title="Telegram"
                    style={{ color: "#2AABEE" }}
                  >
                    <i className="bi bi-telegram" />
                  </a>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pd-share__btn"
                    title="Facebook"
                    style={{ color: "#1877F2" }}
                  >
                    <i className="bi bi-facebook" />
                  </a>
                  <button
                    className={`pd-share__btn${copied ? " pd-share__btn--copied" : ""}`}
                    onClick={handleCopy}
                    title={copied ? "Copied!" : "Copy link"}
                  >
                    <i
                      className={`bi bi-${copied ? "check-lg" : "link-45deg"}`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Price block */}
            <div className="pd-price">
              <span className="pd-price__current">
                {formatCurrency(displayPrice, currency)}
              </span>
              {showDiscount && (
                <>
                  <span className="pd-price__original">
                    {formatCurrency(currentPrice, currency)}
                  </span>
                  <span className="pd-price__badge">-{discountPct}%</span>
                </>
              )}
              <span className="pd-price__free">🚚 Free Delivery</span>
            </div>

            {/* Stock indicator */}
            <div>
              <span className={`pd-stock ${stockLabel.cls}`}>
                <span className="pd-stock__dot" />
                {stockLabel.text}
              </span>
            </div>

            {/* Variant selector */}
            {variantAttrKeys.length > 0 && (
              <div className="pd-variants">
                {variantAttrKeys.map((key) => {
                  const selectedVal = selectedAttrs[key];

                  const isColor =
                    key.toLowerCase().includes("color") ||
                    key.toLowerCase().includes("colour");

                  return (
                    <div key={key} className="pd-attr-group">
                      <div className="pd-attr-group__label">
                        {key}
                        {selectedVal && <span>{selectedVal}</span>}
                      </div>
                      <div className="pd-attr-chips">
                        {(attrOptionsMap[key] || []).map((val) => {
                          const available = isValueAvailable(key, val);
                          const active = selectedVal === val;

                          return (
                            <button
                              key={val}
                              className={[
                                "pd-chip",
                                isColor ? "pd-chip--color" : "",
                                active ? "pd-chip--active" : "",
                                !available ? "pd-chip--disabled" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              title={val}
                              disabled={!available}
                              onClick={() => {
                                setSelectedAttrs((p) => ({ ...p, [key]: val }));
                                setQuantity(1);
                              }}
                              style={isColor ? { backgroundColor: val } : {}}
                            >
                              {!isColor && val}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="pd-cart-box">
              <div className="pd-qty-row">
                <span className="pd-qty-label">Qty</span>
                <div className="pd-qty-ctrl">
                  <button
                    className="pd-qty-ctrl__btn"
                    onClick={decQty}
                    disabled={quantity <= 1}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    className="pd-qty-ctrl__val"
                    value={quantity}
                    min={1}
                    max={maxQty || 1}
                    readOnly
                  />
                  <button
                    className="pd-qty-ctrl__btn"
                    onClick={incQty}
                    disabled={maxReached || stockQty <= 0}
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                className="pd-btn pd-btn--cart"
                onClick={handleAddToCart}
                disabled={stockQty <= 0}
              >
                🛒{" "}
                {maxReached
                  ? "Max Added"
                  : isInCart(selectedVariant?.id)
                    ? "Add More"
                    : "Add to Cart"}
              </button>

              <button
                className="pd-btn pd-btn--buy"
                disabled={stockQty <= 0}
                onClick={() => {
                  handleAddToCart();
                  navigate("/cart");
                }}
              >
                Buy Now
              </button>
            </div>

            {/* Seller card */}
            {product.sellerName && (
              <div className="pd-seller">
                <div className="pd-seller__avatar">{sellerInitial}</div>
                <div>
                  <div className="pd-seller__label">Sold by</div>
                  <div className="pd-seller__name">{product.storeName}</div>
                </div>
              </div>
            )}

            {/* Trust badges */}
            <div className="pd-trust">
              {[
                "Secure & Verified Payment",
                `7-Day Return Policy${product.returnPolicy ? ` · ${product.returnPolicy}` : ""}`,
                product.warrantyPeriod
                  ? `${product.warrantyPeriod} Month Warranty`
                  : "Verified Seller",
              ].map((t) => (
                <div key={t} className="pd-trust__item">
                  <span className="pd-trust__icon">✓</span> {t}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══════════════════════════════════
            TABS
        ══════════════════════════════════ */}
        <div className="pd-tabs">
          <nav className="pd-tabs__nav">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`pd-tabs__btn${activeTab === tab.id ? " pd-tabs__btn--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="pd-tabs__body">
            {/* Description */}
            {activeTab === "description" && (
              <div
                style={{
                  lineHeight: 1.8,
                  fontSize: 15,
                  color: "#374151",
                  maxWidth: 720,
                }}
              >
                {product.description || (
                  <span style={{ color: "#9CA3AF" }}>
                    No description provided.
                  </span>
                )}
              </div>
            )}

            {/* Specifications */}
            {activeTab === "specs" && (
              <table className="pd-specs">
                <tbody>
                  {[
                    ["Brand", product.brand || "—"],
                    ["Model", product.modelNumber || "—"],
                    ["SKU", product.sku || selectedVariant?.sku || "—"],
                    [
                      "Condition",
                      product.productCondition ||
                        selectedVariant?.productCondition ||
                        "—",
                    ],
                    ["Category", product.categoryName || "—"],
                    ["Subcategory", product.subCategoryName || "—"],
                    ["Currency", currency],
                    ...(product.weightUnit
                      ? [["Weight Unit", product.weightUnit]]
                      : []),
                    ...(product.dimensionUnit
                      ? [["Dimension Unit", product.dimensionUnit]]
                      : []),
                    ...(product.shippingClass
                      ? [["Shipping Class", product.shippingClass]]
                      : []),
                    /* Product-level attributes */
                    ...Object.entries(product.attributes || {}),
                    /* Selected variant attributes */
                    ...Object.entries(selectedVariant?.attributes || {}),
                  ].map(([key, val]) => (
                    <tr key={key}>
                      <th>{key}</th>
                      <td>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Reviews — endpoint not yet available */}
            {activeTab === "reviews" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: "40px 24px",
                  gap: 12,
                  textAlign: "center",
                }}
              >
                <span style={{ fontSize: 36 }}>⭐</span>
                <p
                  style={{
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 600,
                    color: "#0D1526",
                  }}
                >
                  Reviews coming soon
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: "#6B7A99",
                    maxWidth: 320,
                  }}
                >
                  Customer reviews will be available here once the feature is
                  live.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ══════════════════════════════════
            RELATED PRODUCTS
        ══════════════════════════════════ */}
        {related.length > 0 && (
          <div className="pd-related">
            <h4>You May Also Like</h4>
            <div className="pd-related-grid">
              {related.slice(0, 8).map((p) => {
                const img = getImageUrl(p.mainImageUrl || p.mainImage);
                const relSlug = `${p.productName?.toLowerCase().replace(/\s+/g, "-")}-${p.productId}`;
                return (
                  <Link
                    key={p.productId}
                    to={`/products/${relSlug}`}
                    className="pd-related-card"
                  >
                    <img
                      src={img || PLACEHOLDER}
                      alt={p.productName}
                      onError={(e) => {
                        e.target.src = PLACEHOLDER;
                      }}
                      loading="lazy"
                    />
                    <div className="pd-related-card__body">
                      <div className="pd-related-card__name">
                        {p.productName}
                      </div>
                      <div className="pd-related-card__price">
                        {formatCurrency(p.price, p.currency)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        <ProductImageLightbox
          images={currentImages.filter(Boolean).map((img) => getImageUrl(img))}
          // images={currentImages.map((img) => getImageUrl(img))}
          initialIndex={lightboxIndex}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      </div>
    </div>
  );
}
